<script lang="ts">
  /**
   * The Live pane: bank and preset, tempo with a tap button, the beat, and what's pending or
   * switched on. Shown and hidden on its own, whatever mode you're in. Its pieces have fixed
   * widths, so a tempo going from 99 to 100 or a longer preset name doesn't shove the switcher
   * beside it around.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { barLength, beatInBarOf, formatBpm, meterLabel } from '../../shared/transport';
  import Icon from './Icon.svelte';

  let { ctl, visible }: { ctl: AppController; visible: boolean } = $props();
  const t = $derived(ctl.transport);
  const bar = $derived(barLength(t));
  const beatInBar = $derived(beatInBarOf(t));
  const auto = $derived(ctl.settings.beatDetection);
  const current = $derived(
    ctl.libraryVersion >= 0 && ctl.currentPresetId
      ? ctl.library.get(ctl.currentPresetId)
      : undefined,
  );
  const name = $derived(current?.name ?? ctl.scene.name);
</script>

<div class="perf" class:hidden={!visible} data-testid="live-pane" data-tour="live-pane">
  <span class="bank">{ctl.libraryVersion >= 0 ? ctl.library.bankName(ctl.bank) : ''}</span>
  <span class="name" title={name}>{name}</span>
  <span class="bpm" data-testid="live-bpm" aria-label="Tempo">{formatBpm(t)}</span>
  <span class="meter" data-testid="live-meter" title="Time signature">{meterLabel(bar)}</span>
  <button
    class="tap"
    data-testid="live-tap"
    title={`Tap the beat${ctl.keyHint('transport.tap')}`}
    onpointerdown={(e) => {
      e.preventDefault();
      ctl.tap();
    }}>Tap</button
  >
  <span class="dots" aria-hidden="true"
    >{#each Array.from({ length: bar }, (_, i) => i) as b (b)}<i
        class:on={t.locked && b === beatInBar}
        class:one={b === 0}
      ></i>{/each}</span
  >
  <button
    class="auto"
    class:on={auto}
    data-testid="live-auto"
    aria-pressed={auto}
    title={auto
      ? `Beat detection is following the music. Click to hold this tempo${ctl.keyHint('transport.detect')}`
      : `Tempo held: only taps and nudges change it. Click to follow the music again${ctl.keyHint('transport.detect')}`}
    onclick={() => ctl.setBeatDetection(!auto)}>{auto ? 'Auto' : 'Hold'}</button
  >
  {#if ctl.pending}<span class="chip accent">{ctl.pending}</span>{/if}
  {#if ctl.settings.autopilot}<span class="chip" title={ctl.autoLast ?? 'Auto show is on'}
      >auto</span
    >{/if}
  {#if ctl.recordingSince !== null}<span class="chip warn"><Icon name="record" /> REC</span>{/if}
  {#if ctl.blackout}<span class="chip warn">BLACKOUT</span>{/if}
  {#if ctl.freeze}<span class="chip warn">FROZEN</span>{/if}
  {#if ctl.performance}<button class="link" onclick={() => ctl.setPerformance(false)}
      >exit (Esc ×2)</button
    >{/if}
  <button
    class="icon-btn"
    aria-label="Hide the Live pane"
    title={`Hide${ctl.keyHint('ui.livePane')}`}
    onclick={() => ctl.setPane('live', false)}><Icon name="close" /></button
  >
</div>

<style>
  .tap,
  .auto {
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
  }
  .tap:active {
    background: var(--accent);
    color: #000;
  }
  .auto {
    width: 4.2em;
    color: var(--faint);
  }
  .auto.on {
    color: var(--accent);
    border-color: color-mix(in srgb, var(--accent) 50%, transparent);
  }
  .perf {
    pointer-events: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.55);
    border: 1px solid var(--border);
    font-size: 12.5px;
    transition: opacity 0.4s;
  }
  .perf.hidden {
    opacity: 0;
    pointer-events: none;
  }
  .bank {
    color: var(--faint);
    width: 7em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .name {
    font-weight: 600;
    width: 14em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .bpm,
  .meter {
    font-family: var(--mono);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .bpm {
    width: 5ch;
  }
  .meter {
    width: 3ch;
    color: var(--faint);
  }
  .dots {
    display: flex;
    gap: 4px;
    /* Room for up to seven beats, so 3/4 and 4/4 take the same space. */
    width: calc(7 * 7px + 6 * 4px);
  }
  .dots i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
  }
  .dots i.one {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  }
  .dots i.on {
    background: var(--accent);
  }
</style>
